import { useCallback, useMemo } from 'react';

import type { CompositeFilterDescriptor, FilterDescriptor, State } from '@progress/kendo-data-query';
import type { GridColumnMenuProps } from '@progress/kendo-react-grid';

import { DateRangeColumnMenu } from 'core/ui';
import type { DateRangeFilterType, DateRangeFilterValue } from 'core/ui/DateRangeFilter';
import { getDateForUI, isValidDateForUI } from 'core/utils/date-serialization';

import { type RelativeDateFilterType, getRelativeDateRange } from 'features/worklist/utils/relative-date-filters';

export interface DateRangeColumnServiceProps {
  /**
   * Current grid data state
   */
  dataState: State;

  /**
   * Callback when data state changes
   */
  onDataStateChange: (newDataState: State) => void;

  /**
   * Field name for the date column
   */
  field: string;

  /**
   * Title for the filter dialog
   */
  title: string;
}

/**
 * Service hook for managing date range column filtering
 */
export function useDateRangeColumnService({ dataState, onDataStateChange, field, title }: DateRangeColumnServiceProps) {
  // Extract current filter value from data state
  const currentFilterValue = useMemo((): DateRangeFilterValue | null => {
    if (!dataState?.filter?.filters || !Array.isArray(dataState.filter.filters)) {
      return null;
    }

    // Look for date range filters on this field
    const fromFilter = dataState.filter.filters.find(
      (filter: FilterDescriptor | CompositeFilterDescriptor) => 'field' in filter && filter.field === field && filter.operator === 'gte',
    ) as FilterDescriptor | undefined;

    const toFilter = dataState.filter.filters.find(
      (filter: FilterDescriptor | CompositeFilterDescriptor) => 'field' in filter && filter.field === field && filter.operator === 'lte',
    ) as FilterDescriptor | undefined;

    if (fromFilter || toFilter) {
      return {
        type: 'custom' as const,
        fromDate: fromFilter?.value ? getDateForUI(fromFilter.value) : null,
        toDate: toFilter?.value ? getDateForUI(toFilter.value) : null,
      };
    }

    return null;
  }, [dataState, field]);

  // Handle filter changes
  const handleFilterChange = useCallback(
    (filterValue: DateRangeFilterValue | null) => {
      if (!onDataStateChange) return;

      const newDataState = { ...dataState };

      // Initialize filter structure if needed
      if (!newDataState.filter) {
        newDataState.filter = { logic: 'and', filters: [] };
      }

      // Remove existing filters for this field
      newDataState.filter.filters = newDataState.filter.filters.filter(
        (filter: FilterDescriptor | CompositeFilterDescriptor) => !('field' in filter) || filter.field !== field,
      );

      // Add new date range filters if provided
      if (filterValue) {
        let fromDate: Date | null = null;
        let toDate: Date | null = null;

        if (filterValue.type === 'custom') {
          fromDate = filterValue.fromDate || null;
          toDate = filterValue.toDate || null;
        } else {
          // Use getRelativeDateRange for relative date filters
          const dateRange = getRelativeDateRange(filterValue.type as RelativeDateFilterType);
          if (dateRange) {
            fromDate = dateRange.startDate;
            toDate = dateRange.endDate;
          }
        }

        // Add from date filter - keep as Date object for internal consistency
        if (fromDate && isValidDateForUI(fromDate)) {
          newDataState.filter.filters.push({
            field,
            operator: 'gte',
            value: fromDate,
          });
        }

        // Add to date filter (set to end of day) - keep as Date object for internal consistency
        if (toDate && isValidDateForUI(toDate)) {
          const endOfDay = new Date(toDate);
          endOfDay.setHours(23, 59, 59, 999);
          newDataState.filter.filters.push({
            field,
            operator: 'lte',
            value: endOfDay,
          });
        }
      }

      onDataStateChange(newDataState);
    },
    [dataState, onDataStateChange, field],
  );

  // Create the column menu component factory
  const createDateRangeColumnMenu = useCallback(
    (props: GridColumnMenuProps) => (
      <DateRangeColumnMenu
        {...props}
        title={`Filter ${title}`}
        getDateRange={getRelativeDateRange}
        currentFilterValue={currentFilterValue}
        onFilterChange={handleFilterChange}
      />
    ),
    [title, currentFilterValue, handleFilterChange],
  );

  return {
    currentFilterValue,
    handleFilterChange,
    createDateRangeColumnMenu,
  };
}

/**
 * Apply a date range filter to the grid data state
 */
export function applyDateRangeFilterToDataState(dataState: State, field: string, value: DateRangeFilterValue | null): State {
  const newDataState = { ...dataState };

  if (!newDataState.filter) {
    newDataState.filter = {
      logic: 'and',
      filters: [],
    };
  }

  // Remove any existing filters for this field
  newDataState.filter.filters = newDataState.filter.filters.filter((filter) => {
    if ('field' in filter) {
      return filter.field !== field;
    }
    return true;
  });

  // Add new date range filter if value is provided
  if (value) {
    try {
      let fromDate: Date | null = null;
      let toDate: Date | null = null;

      if (value.type === 'custom') {
        fromDate = value.fromDate || null;
        toDate = value.toDate || null;
      } else {
        // Use getRelativeDateRange for relative date filters
        const dateRange = getRelativeDateRange(value.type);
        fromDate = dateRange.startDate;
        toDate = dateRange.endDate;
      }

      // Add date range filter as individual filter descriptors
      if (fromDate && isValidDateForUI(fromDate)) {
        newDataState.filter.filters.push({
          field,
          operator: 'gte',
          value: fromDate,
        });
      }

      if (toDate && isValidDateForUI(toDate)) {
        // Set to end of day for the "to" date
        const endOfDay = new Date(toDate);
        endOfDay.setHours(23, 59, 59, 999);
        newDataState.filter.filters.push({
          field,
          operator: 'lte',
          value: endOfDay,
        });
      }
    } catch (error) {
      console.error('Error calculating date range:', error);
    }
  }

  return newDataState;
}

/**
 * Extract date range filter values from the current data state
 */
export function extractDateRangeStatesFromDataState(dataState: State, dateFields: string[]): Record<string, DateRangeFilterValue | null> {
  const states: Record<string, DateRangeFilterValue | null> = {};

  if (!dataState.filter?.filters) {
    return states;
  }

  dateFields.forEach((field) => {
    const fieldFilters = dataState.filter!.filters.filter((filter) => 'field' in filter && filter.field === field) as FilterDescriptor[];

    if (fieldFilters.length > 0) {
      // Extract from and to dates from filters
      const gteFilter = fieldFilters.find((f) => f.operator === 'gte');
      const lteFilter = fieldFilters.find((f) => f.operator === 'lte');

      if (gteFilter || lteFilter) {
        states[field] = {
          type: 'custom' as DateRangeFilterType,
          fromDate: gteFilter?.value ? getDateForUI(gteFilter.value) : null,
          toDate: lteFilter?.value ? getDateForUI(lteFilter.value) : null,
        };
      }
    }
  });

  return states;
}
